import {
  getIngredientsByIngredientStockId,
  GetProteinFormulationDict,
  GetSeedsDict,
  LayerType,
} from 'utils/Global';
import {
  applyPrecision,
  precisionConcentration,
  precisionPH,
  precisionVolume,
  significantDigitsConcentration,
  significantDigitsPH,
  significantDigitsVolume,
} from '../constants';
import { WellIng } from 'components/Experiment/constants';
import { DrawingLayer } from 'business/models/DrawingLayer';
import { FragmentLayerDTO, ScreenLayerDTO } from 'business/models/Models';

export const getProteinNameByDropNumber = (
  ingredientWell: WellIng,
  dropNumber: number,
) => {
  const proteinFormulations = GetProteinFormulationDict();
  const dropList = ingredientWell?.dropList;
  const protienFormulationId = dropList?.filter((x) => x.dropNumber === dropNumber)[0]
    ?.proteinFormulationID;
  return protienFormulationId
    ? `${proteinFormulations[protienFormulationId]?.pfName} p`
    : '';
};

export const getDropScreenNamesAndVolsByDropNumber = (
  layers: DrawingLayer[],
  wellData: WellIng,
  dropNumber: number,
) => {
  const drop = wellData?.dropList?.find((x) => x.dropNumber === dropNumber);
  if (!drop) {
    return [];
  }

  const screenLayers = layers.filter(
    (layer) => layer.type === LayerType.Screen && layer.targetDrop === drop.dropNumber,
  );

  return screenLayers?.map((layer) => {
    const dropLayer = drop.dropLayerList?.find((l) => l.layerId === layer.id);

    return {
      name: dropLayer
        ? (layer.layerDetail as ScreenLayerDTO)?.screenLot.experiment.expName + ' a'
        : undefined,
      volume: dropLayer?.volume || 0,
    };
  });
};

export const getFragmentNameByDropNumber = (
  ingredientWell: WellIng,
  dropNumber: number,
) => {
  const dropList = ingredientWell?.dropList;
  const dropLayersList = dropList?.filter((x) => x.dropNumber === dropNumber)[0]
    ?.dropLayerList;
  const fragNames: string[] = [];

  dropLayersList?.forEach((dl) => {
    dl.dropLayerFragmentList?.forEach((dlf) => fragNames.push(`${dlf.name} f`));
  });

  return fragNames.join(', ');
};

export const getFragmentScreenNameByDropNumber = (
  layers: DrawingLayer[],
  ingredientWell: WellIng,
  dropNumber: number,
) => {
  const drop = ingredientWell?.dropList?.find((d) => d.dropNumber === dropNumber);
  if (!drop) {
    return;
  }

  const matchedLayer = layers?.find(
    (layer) =>
      (layer.layerDetail as FragmentLayerDTO)?.fragmentScreenId === drop.fragmentScreenId,
  );

  const fragmentScreenName = (matchedLayer?.layerDetail as FragmentLayerDTO)
    ?.fragmentScreen?.treeNode?.name;

  if (!fragmentScreenName) {
    return;
  }

  return `${fragmentScreenName} f`;
};

export const getSeedNameByDropNumber = (ingredientWell: WellIng, dropNumber: number) => {
  const seeds = GetSeedsDict();
  const dropList = ingredientWell?.dropList;
  const seedId = dropList?.filter((x) => x.dropNumber === dropNumber)[0]?.seedID;
  return seedId ? `${seeds[seedId]?.name} s` : '';
};

export const getWellScreenLayerName = (layers, wellData: WellIng) => {
  const wellLayerIds = wellData.wellLayerList.map((wl) => wl.layerId);
  const wellScreenLayer = layers.find(
    (l) => l.type === LayerType.Screen && wellLayerIds.includes(l.id),
  );

  return wellScreenLayer ? wellScreenLayer.name : '';
};

export const generateWellTooltipText = (wellData: WellIng): string => {
  if (!wellData.wellLayerList.length) {
    return null;
  }
  const boldMarker = '\u0001'; // Add after the text that should be bold, will be parsed by tooltip component
  let text = `Water${boldMarker}: ${wellData.wellWaterVolume} µL\n`;
  wellData.wellLayerList?.forEach((wellLayer) => {
    wellLayer.wellLayerIngredientList?.forEach((wellLayerIngredient) => {
      const { stock, ingredient } = getIngredientsByIngredientStockId(
        wellLayerIngredient.ingredientStockId,
      );
      text += `${ingredient?.longName}${boldMarker}, `;
      text += `${applyPrecision(
        wellLayerIngredient.concentration,
        significantDigitsConcentration,
        precisionConcentration,
      )}${stock?.concentrationUnits}, `;
      text += `${applyPrecision(
        wellLayerIngredient.volume,
        significantDigitsVolume,
        precisionVolume,
      )} µL`;
      text += applyPrecision(wellLayerIngredient.pH, significantDigitsPH, precisionPH)
        ? `, ${applyPrecision(
            wellLayerIngredient.pH,
            significantDigitsPH,
            precisionPH,
          )} pH`
        : '';
      text += '\n';
    });
  });

  return text.trimEnd();
};
